import * as R from 'ramda';

import type { MapFilter, MapInitialized, MapInstance, MapLayerLike } from './types.ts';
import { getMapState, keyToLayerStyle } from './utils.ts';

type SelectableLayerGroup = {
  nothingSelected: MapLayerLike;
  selected: MapLayerLike;
  notSelected: MapLayerLike;
  forHighlight: boolean;
};

type MapSetFilter = Parameters<MapInstance['setFilter']>[1];
type ExistingSelectionFilter = MapFilter | undefined;

const getSelectionFilter = (map: MapInstance, layerId: string): ExistingSelectionFilter => {
  const filter = map.getFilter(layerId);
  return Array.isArray(filter) ? (filter[2] as ExistingSelectionFilter) : undefined;
};

export default function SelectionController(
  app: {
    bus: {
      on: <TPayload = unknown>(eventName: string, handler: (payload: TPayload) => unknown) => void;
      send: <TPayload = unknown>(eventName: string, payload?: TPayload) => unknown;
    };
  },
  mapInitialized: MapInitialized,
) {
  const selectableLayers: SelectableLayerGroup[] = [];

  const updateLayers = (map: MapInstance, entityIds: Array<string | number>, isHiglighting = false): void => {
    const isSomethingSelected = entityIds.length > 0;
    selectableLayers.forEach(layer => {
      const selectedId = layer.selected.id;
      const selectedFilters = getSelectionFilter(map, selectedId);
      if (isHiglighting && !layer.forHighlight) {
        map.setFilter(selectedId, ['all', false, selectedFilters] as unknown as MapSetFilter);
      } else {
        map.setFilter(selectedId, [
          'all',
          isSomethingSelected ? ['match', ['get', 'id'], entityIds, true, false] : false,
          selectedFilters,
        ] as unknown as MapSetFilter);
      }
      const notSelectedLayerId = layer.notSelected.id;
      const notSelectedFilters = getSelectionFilter(map, notSelectedLayerId);
      map.setFilter(notSelectedLayerId, [
        'all',
        isSomethingSelected ? ['match', ['get', 'id'], entityIds, false, true] : false,
        notSelectedFilters,
      ] as unknown as MapSetFilter);
      const nothingSelectedLayerId = layer.nothingSelected.id;
      const nothingSelectedFilters = getSelectionFilter(map, nothingSelectedLayerId);
      map.setFilter(nothingSelectedLayerId, [
        'all',
        !isSomethingSelected,
        nothingSelectedFilters,
      ] as unknown as MapSetFilter);
    });
    app.bus.send('map/entitySelectionUpdated', getMapState(map));
  };

  const selectEntities = (ids: Array<string | number>): void => {
    mapInitialized().then((map: MapInstance) => {
      updateLayers(map, R.uniq(ids), false);
    });
  };

  const highlightEntities = (ids: Array<string | number>): void => {
    mapInitialized().then((map: MapInstance) => {
      updateLayers(map, R.uniq(ids), true);
    });
  };

  const initMapLayers = (layers: MapLayerLike[]): MapLayerLike[] => {
    selectableLayers.length = 0;
    return layers.flatMap(layer => {
      const selection = layer.metadata?.selection;
      if (selection) {
        let selectedLayer: MapLayerLike = { ...layer, id: `${layer.id}_selected` };
        R.mapObjIndexed((value, key) => {
          const path = [keyToLayerStyle(key), key];
          selectedLayer = R.assocPath(path, value?.selected, selectedLayer) as MapLayerLike;
        }, selection);
        selectedLayer.filter = ['all', false, selectedLayer.filter];

        let notSelectedLayer: MapLayerLike = { ...layer, id: `${layer.id}_notSelected` };
        R.mapObjIndexed((value, key) => {
          const path = [keyToLayerStyle(key), key];
          notSelectedLayer = R.assocPath(path, value?.notSelected, notSelectedLayer) as MapLayerLike;
        }, selection);
        notSelectedLayer.filter = ['all', false, notSelectedLayer.filter];

        selectableLayers.push({
          nothingSelected: layer,
          selected: selectedLayer,
          notSelected: notSelectedLayer,
          forHighlight: ['polygons', 'polygons - counters'].includes(layer.id),
        });

        layer.filter = ['all', true, layer.filter];
        return [selectedLayer, notSelectedLayer, layer];
      }
      return [layer];
    });
  };

  app.bus.on('map/selectEntities', ({ ids }: { ids: Array<string | number> }) => selectEntities(ids));

  app.bus.on('map/highlightEntities', ({ ids }: { ids: Array<string | number> }) => highlightEntities(ids));

  app.bus.on('map/cleanMap', () => {
    mapInitialized().then((map: MapInstance) => {
      updateLayers(map, []);
    });
  });

  return { initMapLayers };
}
